import h from 'hyperscript';
import filterIcon from '@disy/cadenza-icons/filter.svg';

import { AFTER_LOAD_DATA } from 'ui/data-loading-event-types';
import { setupContextMenu } from 'ui/menu/menu';

import { getRowDataOfRows } from 'cadenza/ui/base-table/base-table';
import { ResultTablePlugin } from 'cadenza/result-table/result-table-plugin';
import { icon } from 'cadenza/utils/icon/icon';
import { on, unByKey } from 'cadenza/utils/event-util';
import { hashCode } from 'cadenza/utils/hash-code';
import {
  getRowsWithSelection,
  isRowSelection,
  ResultTableSelectionChangeEvent
} from 'cadenza/result-table/selection/selection-result-table-plugin';
import { logger } from 'cadenza/utils/logging';

import './custom-filtering-result-table-plugin.css';
import i18n from './custom-filtering-result-table-plugin.properties';

const CUSTOM_FILTERING_CONDITION_NAME = 'net.disy.selector.custom_table_condition';

const COMPONENT_NAME = 'd-custom-filtering-result-table-plugin';

class CustomFilteringResultTablePlugin extends ResultTablePlugin {

  /**
   * @type {Function}
   * @private
   */
  _removeCurrentCustomFilteringMenu;

  _eventKeys;
  _lastSelectedRows;
  _customTableCondition;

  init () {
    this._eventKeys = [
      on(this.table, AFTER_LOAD_DATA, () => this._enableCustomFiltering(), { once: true }),
      on(
        this.table,
        ResultTableSelectionChangeEvent.TYPE,
        (event) => this._onSelectionChange(event))
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  _enableCustomFiltering () {
    logger.debug('Enable custom filtering');
    this._customTableCondition = document.querySelector(`d-table-condition[condition-name='${CUSTOM_FILTERING_CONDITION_NAME}']`);
    this._resetSelectedRows();
  }

  _onSelectionChange (event) {
    const selection = event.selection;
    if (isRowSelection(selection)) {
      const selectedRowsCount = selection.size;
      const rowIds = getRowsWithSelection(selection);
      const selectedRows = getRowDataOfRows(this.table.getDataTable(), rowIds);
      this._removeCurrentCustomFilteringMenu = setupContextMenu(event.lastClickedCell.tdElement, () => {
        const menuStructure = [
          h('h1.d-dropdown-heading', i18n('info', { count: selectedRowsCount })),
          h('button.button.button-borderless.button-action', {
            'type': 'button',
            'title': i18n('buttonText'),
            'onclick': () => {
              this._onCustomFiltering();
            }
          }, [ icon(filterIcon), i18n('buttonText') ])
        ];
        return menuStructure;
      }, {
        appendTo: document.body,
        initiallyOpen: true,
        onClose: () => this._resetSelectedRows(),
        styleClass: `${COMPONENT_NAME}--menu`
      });

      this._lastSelectedRows = selectedRows;
    }
  }

  _resetSelectedRows () {
    this._lastSelectedRows = [];
  }

  _onCustomFiltering () {
    this._customTableCondition.value = this._getSelectedRows();
    this._customTableCondition.onConditionChange();
    if (this._removeCurrentCustomFilteringMenu) {
      this._removeCurrentCustomFilteringMenu();
      this._removeCurrentCustomFilteringMenu = undefined;
    }
  }

  _getSelectedRows () {
    const relevantIndices = this.resultTableColumnIndices;

    return this._lastSelectedRows
      .map(rowData => {
        const value = getRelevantValues(rowData.values, relevantIndices);
        const formatted = getRelevantValues(rowData.formatted, relevantIndices);
        const id = hashCode(JSON.stringify(rowData.values));
        const text = getRowText(rowData.formatted, relevantIndices);
        return { value, formatted, id, text };
      });
  }

  get resultTableColumnIndices () {
    return this.table.customConditionResultTableAttributeIndices.map(item => {
      const newItem = Object.assign({}, item);
      newItem.index += 1; // + 1 because of row index column
      return newItem;
    });
  }

}

function getRelevantValues (values, relevantIndices) {
  return relevantIndices.map(i => values[i.index]);
}

function getRowText (rowFormattedValues, resultTableColIndices) {
  const visibleFormattedValues = resultTableColIndices.map(item => item.visible ? rowFormattedValues[item.index] : null);
  return visibleFormattedValues.filter(v => v != null).join(', ');
}

customElements.define(COMPONENT_NAME, CustomFilteringResultTablePlugin);
